import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Noise } from "@/components/Noise";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  { key: "all", label: "All" },
  { key: "mobile_home_seller", label: "Mobile Sellers" },
  { key: "buyer_investor", label: "Buyers/Investors" },
  { key: "real_estate", label: "Real Estate" },
  { key: "contact", label: "General" },
];
const STATUSES = ["new", "contacted", "closed"];
const typeLabel = {
  real_estate: "Real Estate", credit: "Credit", contact: "General",
  mobile_home_seller: "Mobile Seller", buyer_investor: "Buyer/Investor",
};
const FIELD_LABELS = {
  name: "Name", phone: "Phone", email: "Email", property_address: "Location",
  property_type: "Property Type", intent: "Looking To", goal: "Primary Goal",
  help_with: "Wants Help With", contact_method: "Preferred Contact",
  year: "Year", bedrooms: "Bedrooms", bathrooms: "Bathrooms", size: "Approx. Size",
  condition: "Condition", placement: "Placement", move_status: "Stay / Move",
  timeline: "Timeline", investor_type: "Buyer Type", markets: "Markets",
  property_interest: "Property Interest", budget: "Budget", financing: "Financing",
  message: "Message",
};

export default function Admin() {
  const { user, checking, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (checking || !user?.role) return;
    api
      .get("/leads")
      .then(({ data }) => setLeads(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [checking, user]);

  const filtered = useMemo(
    () => (filter === "all" ? leads : leads.filter((l) => l.lead_type === filter)),
    [leads, filter]
  );

  const counts = useMemo(() => {
    const c = { all: leads.length, real_estate: 0, credit: 0, contact: 0 };
    leads.forEach((l) => (c[l.lead_type] = (c[l.lead_type] || 0) + 1));
    return c;
  }, [leads]);

  const updateStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  if (checking) return <div className="min-h-screen grid place-items-center text-foreground/50">Loading…</div>;
  if (!user?.role) return <Navigate to="/login" replace />;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-secondary/30">
      <Noise />
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl tracking-mega">FaeVor</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">Works · Leads</span>
          </div>
          <Button
            data-testid="admin-logout"
            variant="ghost"
            onClick={logout}
            className="gap-2 text-foreground/70 hover:text-foreground"
          >
            <LogOut size={16} /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl sm:text-5xl tracking-mega">Leads</h1>
        <p className="text-foreground/60 mt-2">Every enquiry from across the site, in one place.</p>

        <div className="mt-8 flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setFilter(t.key)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                filter === t.key
                  ? "bg-accent text-accent-foreground"
                  : "border border-border text-foreground/70 hover:border-primary"
              }`}
            >
              {t.label} <span className="opacity-60">({counts[t.key] || 0})</span>
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-background overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-foreground/50">Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-foreground/50 flex flex-col items-center gap-3">
              <Inbox size={32} strokeWidth={1.4} />
              No leads yet.
            </div>
          ) : (
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden sm:table-cell">Contact</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="cursor-pointer"
                    onClick={() => setActive(l)}
                  >
                    <TableCell className="text-foreground/60 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </TableCell>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell>
                      <span className="text-xs uppercase tracking-wide text-primary font-semibold">
                        {typeLabel[l.lead_type]}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-foreground/70">
                      {l.email || l.phone || "—"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-${l.id}`} className="h-8 w-32 text-xs capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="lead-detail" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {active?.name}{" "}
              <span className="text-sm text-primary uppercase tracking-wide">
                · {active && typeLabel[active.lead_type]}
              </span>
            </DialogTitle>
            <DialogDescription>Full details for this lead submission.</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="divide-y divide-border">
              {Object.entries(FIELD_LABELS).map(([k, label]) =>
                active[k] ? (
                  <div key={k} className="grid grid-cols-[130px_1fr] gap-3 py-2.5 text-sm">
                    <span className="text-foreground/50">{label}</span>
                    <span className="text-foreground">{active[k]}</span>
                  </div>
                ) : null
              )}
              <div className="grid grid-cols-[130px_1fr] gap-3 py-2.5 text-sm">
                <span className="text-foreground/50">Received</span>
                <span>{new Date(active.created_at).toLocaleString()}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
